import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {} from 'lucide-react'
import Cta from '@/components/storefront/Cta'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import type { HeroSlide } from '@/types'

const AUTOPLAY_MS = 6000


/**
 * Full-bleed hero carousel. Slides are admin-managed (admin > Hero); the text
 * is rendered as HTML over the image rather than baked into the artwork, so it
 * stays editable and readable to screen readers.
 *
 * Height is bounded rather than a fixed aspect ratio: a hard 2:1 frame
 * letterboxes tall phones and grows absurd on a wide monitor. The height is
 * reserved up front so the page does not jump when the first image decodes.
 */
export default function HeroCarousel() {
  const slides = useQuery({
    queryKey: ['storefront-hero'],
    queryFn: async () => (await api.get<{ data: HeroSlide[] }>('/storefront/hero-slides')).data.data,
  })

  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const reduce = useReducedMotion()
  // Autoplay only while the hero is on screen. A crossfade swaps in and
  // decodes a full-screen photo, and doing that while the visitor scrolls
  // the rest of the page is wasted work that phones feel as stutter.
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref)

  const rows = slides.data ?? []
  const count = rows.length

  useEffect(() => {
    if (count <= 1 || paused || !inView) return
    const id = setInterval(() => setIndex((i) => (i + 1) % count), AUTOPLAY_MS)
    return () => clearInterval(id)
  }, [count, paused, inView])

  // Nothing configured yet: fall back to a plain band so the page is never
  // headless while an admin is still setting slides up.
  if (!slides.isLoading && count === 0) {
    return (
      <section className="bg-primary bg-noise text-white">
        <div className="mx-auto w-full max-w-6xl px-5 py-20 text-center">
          <h1 className="text-4xl font-medium leading-tight sm:text-5xl">
            Premium apparel printing for teams and businesses
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-white/70">
            Configure jerseys, corporate wear, caps and jackets with names and numbers, see your price instantly.
          </p>
          <Cta to="/products" variant="white" arrow className="mt-8">
            Browse products
          </Cta>
        </div>
      </section>
    )
  }

  const safeIndex = Math.min(index, Math.max(count - 1, 0))
  const current = rows[safeIndex]

  return (
    <section
      ref={ref}
      // Fills the screen below the header (56px on mobile, 70px from sm up), so
      // the photograph is the whole first impression. svh, not vh or dvh: vh
      // ignores browser chrome so the CTA ends up under it, and dvh changes as
      // the address bar slides away, resizing the hero and reflowing the whole
      // page mid-scroll. svh is the height with the chrome showing, and fixed.
      //
      // The background shows before the first image decodes and through each
      // crossfade, so it is the page colour, not primary - a full-bleed blue
      // flash on load reads as a broken slide rather than a loading one.
      className="relative h-[calc(100svh-56px)] min-h-[480px] w-full overflow-hidden bg-background sm:h-[calc(100svh-70px)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Featured"
    >
      <AnimatePresence initial={false}>
        {current && (
          <motion.div
            key={current.id}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.7, ease: 'easeInOut' }}
          >
            <img
              src={current.image_path}
              alt=""
              // The first slide is the page's largest paint; the rest can wait.
              fetchPriority={safeIndex === 0 ? 'high' : 'low'}
              loading={safeIndex === 0 ? 'eager' : 'lazy'}
              decoding="async"
              className="size-full object-cover"
            />
            {/* Scrim is bottom-weighted and clears entirely by halfway up, so
                the photograph stays bright. A wash over the whole image reads
                as a dark band rather than a photo with text on it. */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 via-40% to-transparent" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content sits in the bottom third, not vertically centred. The bottom
          padding leaves room for the dots underneath. */}
      {current && (
        <div className="relative flex h-full flex-col items-center justify-end px-5 pb-20 text-center text-white sm:pb-24">
          {current.eyebrow && (
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{current.eyebrow}</p>
          )}
          {/* Archivo 800 with tight negative tracking is what makes the
              reference headline land. Uppercased in CSS so any admin copy
              reads as a statement. */}
          <h1 className="max-w-4xl text-3xl font-extrabold uppercase leading-[0.95] tracking-[-0.02em] drop-shadow-lg sm:text-5xl lg:text-7xl">
            {current.title}
          </h1>
          {current.subtitle && (
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/85 drop-shadow sm:text-base">
              {current.subtitle}
            </p>
          )}
          {current.cta_label && current.cta_url && (
            <div className="mt-7">
              <Cta to={current.cta_url} variant="white" size="xl" arrow className="shadow-lg">
                {current.cta_label}
              </Cta>
            </div>
          )}
        </div>
      )}

      {count > 1 && (
        <div className="absolute inset-x-0 bottom-5 flex justify-center gap-2">
          {rows.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === safeIndex ? 'true' : undefined}
              onClick={() => setIndex(i)}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === safeIndex ? 'w-8 bg-white' : 'w-4 bg-white/50 hover:bg-white/80',
              )}
            />
          ))}
        </div>
      )}
    </section>
  )
}
